/**
 * My Dealer Service Layer
 * Centralizes all API calls for the My Dealer feature
 * All API endpoints are inlined and self-contained
 */

import axios from "axios";
import { BASE_URL } from "@config/variables";
import { getNodeServerUrl } from "@utils/getNodeUrl";

/**
 * Service class for My Dealer API operations
 */
class MyDealerService {
  /**
   * Get dealers by territory code
   * @param {Object} payload - { code: string, isjointlyadopteddealer: boolean }
   * @returns {Promise} API response
   */
  async getDealers(payload) {
    try {
      const response = await axios({
        url: `${BASE_URL}/services/apexrest/MyDealer/`,
        method: "post",
        data: payload,
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data?.Dealer || [],
          message: "Dealers fetched successfully",
        };
      }

      return {
        success: false,
        data: [],
        message: "Failed to fetch dealers",
      };
    } catch (error) {
      console.error("Error fetching dealers:", error);
      return {
        success: false,
        data: [],
        message: error.message || "Failed to fetch dealers",
      };
    }
  }

  /**
   * Check in to a dealer
   * @param {Object} payload - Complete check-in payload constructed by hook
   * @returns {Promise} API response
   */
  async checkInToDealer(payload) {
    try {
      const response = await axios({
        url: `${BASE_URL}/services/apexrest/CheckIn_VisitPlan/`,
        method: "post",
        data: payload,
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data,
          message: "Check-in successful",
        };
      }

      return {
        success: false,
        data: null,
        message: "Check-in failed",
      };
    } catch (error) {
      console.error("Error checking in:", error);
      const apiError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Check-in failed";
      return {
        success: false,
        data: null,
        message: apiError,
      };
    }
  }

  /**
   * Update an existing check-in record (HO Auditor 2-step flow)
   * Used after initial check-in creates a record, then audit data is added
   * @param {Object} payload - Update payload
   * @param {string} payload.Record_ID - ID of existing check-in record
   * @param {string} [payload.Dealer_Check_in] - Dealer audit option
   * @param {string} [payload.non_darphan_bill_file_url] - Blob URL for uploaded file
   * @param {string} [payload.Retail_counter_present] - WSS: retail counter
   * @param {string} [payload.non_performing_status] - WSS: non-performing
   * @param {string} [payload.Company_Name] - WSS: company name
   * @returns {Promise} API response
   */
  async updateCheckIn(payload) {
    try {
      const response = await axios({
        url: `${BASE_URL}/services/apexrest/CheckIn_VisitPlanUpdate/`,
        method: "post",
        data: payload,
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data,
          message: "Check-in updated successfully",
        };
      }

      return {
        success: false,
        data: null,
        message: "Check-in update failed",
      };
    } catch (error) {
      console.error("Error updating check-in:", error);
      return {
        success: false,
        data: null,
        message: error.message || "Check-in update failed",
      };
    }
  }

  /**
   * Get territories for ZSM users  
   * @param {string} sh3Code - SH3 Code for territory query
   * @returns {Promise} API response
   */
  async getZSMTerritories(sh3Code) {
    try {
      const query = encodeURIComponent(
        `Select id, SH2_Code__c, SH2_Name__c, SH3_Code__c, TXNType__c, SH6_Code__c, SH3_Name__c from Pidilite_Hierarchy__c where SH3_Code__c='${sh3Code}' AND RecordType.Name In('Sales Hierarchy','Key Information')`
      );

      const response = await axios({
        url: `${BASE_URL}/services/data/v56.0/query?q=${query}`,
        method: "get",
      });

      if (response.status === 200) {
        return {
          success: true,
          data: response?.data?.records || [],
          message: "Territories fetched successfully",
        };
      }

      return {
        success: false,
        data: [],
        message: "Failed to fetch territories",
      };
    } catch (error) {
      console.error("Error fetching ZSM territories:", error);
      return {
        success: false,
        data: [],
        message: error.message || "Failed to fetch territories",
      };
    }
  }
  /**
   * Trigger real-time check-in verification for SH3 auto-approve
   * Always resolves — never throws — so check-in is never blocked
   * @param {string} dealerCode
   * @param {string} tsiCode
   * @param {string} userTitle  - e.g. "SH3"
   * @param {string} userEmail
   * @returns {Promise} { success, verified, msg }
   */
  async triggerCheckInVerification(dealerCode, tsiCode, userTitle, userEmail) {
    try {
      const nodeBaseUrl = getNodeServerUrl();
      const response = await axios({
        url: `${nodeBaseUrl}/dealer/checkInVerification`,
        method: "post",
        data: { dealerCode, tsiCode, userTitle, userEmail },
      });

      return response?.data || { success: true, verified: false };
    } catch (error) {
      console.error("triggerCheckInVerification error:", error);
      // Always resolve — check-in itself must not be blocked
      return { success: true, verified: false, msg: error.message };
    }
  }

  /**
   * Historical auto-approve: check pending dealers in given territories
   * against Salesforce SH3 check-in history (last 90 days).
   * Always resolves — never throws.
   * @param {string[]} tsiCodes - Array of TSI/territory codes
   * @param {string} userEmail - Logged-in user's email (used for the SF JWT auth)
   * @returns {Promise<{success, autoApprovedCount, totalPendingChecked}>}
   */
  async verifyPendingDealersViaHistory(tsiCodes, userEmail) {
    try {
      const nodeBaseUrl = getNodeServerUrl();
      const response = await axios({
        url: `${nodeBaseUrl}/dealer/verifyPendingDealersViaHistory`,
        method: "post",
        data: { TTYCodesList: tsiCodes, userEmail },
      });

      return response?.data || { success: true, autoApprovedCount: 0, totalPendingChecked: 0 };
    } catch (error) {
      console.error("verifyPendingDealersViaHistory error:", error);
      return { success: true, autoApprovedCount: 0, totalPendingChecked: 0 };
    }
  }
}

// Export singleton instance
export const myDealerService = new MyDealerService();
export default myDealerService;
